import exception from "@/utils/exception";
import Constants from "src/utils/constants";
import Helper from "@/utils/helper";
import { getCollection } from "@/plugins/rxdb/database";
import { useRxDBStore } from "src/stores/rxDBStore";

// At the module level, add a cache for count results
const countCache = new Map();

// Function to create a cache key based on query parameters
function createCacheKey(tableName, fetchDeleted, searchTxt, queryParams) {
  return JSON.stringify({
    table: tableName,
    deleted: fetchDeleted,
    search: searchTxt,
    params: queryParams
  });
}

function updateLocalSyncQueue(tableName, id, isSynced = false) {
  useRxDBStore().updateLocalSyncQueue(tableName, id, isSynced);
}

/**
 * Inserts a new record into the specified RxDB collection.
 * Automatically generates a UUID and adds creation timestamp.
 *
 * @param {string} tableName - The name of the collection to insert data into
 * @param {Object} obj - The object containing the data to insert
 *
 * @returns {Promise<Object>} Returns an object with the following properties:
 *   - error: Error message if any, or null if successful
 *   - id: The ID of the newly inserted record, or null if insertion failed
 *
 * @example
 * const result = await insertData('users', {
 *   name: 'John Doe',
 *   email: 'john@example.com'
 * });
 */
async function insertData(tableName, obj) {
  try {
    const collection = await getCollection(tableName);

    const formEntry = {
      id: Helper.getRandomUuid(),
      ...obj,
      is_deleted: false,
      remote_updated_at: new Date().toISOString(),
      local_updated_at: new Date().toISOString(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      required_sync: true
    };

    // update local sync queue
    updateLocalSyncQueue(tableName, formEntry.id);
    const result = await collection.insert(formEntry);
    return {
      error: null,
      id: result ? result.id : null // Adjust according to your table's schema
    };
  } catch (error) {
    // if error occurs in the exceptions table then don't catch the exception
    if (tableName === Constants.enums.TABLES_NAME_ENUM.EXCEPTIONS_TABLE) {
      console.log("ERROR IN INSERT DATA :: EXCEPTIONS TABLE", error);
    } else {
      exception.catchException(error);
    }
    return error;
  }
}

/**
 * Subscribe to a document in a collection and add a callback to be called when the document is updated.
 * @param {string} tableName - The name of the collection to subscribe to
 * @param {string} id - The id of the document to subscribe to
 * @param {Function} callback - The callback to be called when the document is updated
 */
async function subscribeToDocument(tableName, id, callback) {
  try {
    const collection = await getCollection(tableName);
    const form = await collection
      .findOne({
        selector: {
          id
        }
      })
      .exec();
    const subscription = form.$.subscribe((changeEvent) => {
      callback(changeEvent.permissions, changeEvent);
    });
    return subscription;
  } catch (error) {
    exception.catchException(error);
    return error;
  }
}

/**
 * Updates an existing record in the specified RxDB collection.
 * Automatically updates the local_updated_at timestamp.
 *
 * @param {string} tableName - The name of the collection to update data in
 * @param {Object} obj - The object containing the data to update, must include an 'id' field
 *
 * @returns {Promise<Object>} Returns an object with the following properties:
 *   - error: Error message if any, or null if successful
 *   - data: The updated record, or null if update failed
 *
 * @example
 * const result = await updateData('users', {
 *   id: '123',
 *   name: 'John Smith',
 *   email: 'john.smith@example.com'
 * });
 */
async function updateData(tableName, obj) {
  try {
    console.warn(
      `------------- USING RXDB TO UPDATE DATA : ${tableName} -------------`
    );
    const collection = await getCollection(tableName);

    const form = await collection
      .findOne({
        selector: {
          id: obj.id
        }
      })
      .exec();

    if (form) {
      console.log("INCREMENTAL PATCH :", obj);
      // await form.incrementalPatch({
      //   ...obj,
      //   local_updated_at: new Date().toISOString()
      // });
      await form.incrementalModify(docData => {
        return {
          ...docData,
          ...obj,
          local_updated_at: new Date().toISOString()
        }
      })
      // get DATA
      await getData(tableName, obj.id);
    } else {
      return {
        error: "Form not found",
        data: null
      };
    }

    return {
      error: null,
      data: form.toJSON()
    };
  } catch (error) {
    exception.catchException(error);
    return error;
  }
}

/**
 * Fetches data from a specified table with filtering, sorting, and pagination capabilities.
 * Implements optimized count caching to reduce redundant database queries.
 *
 * @param {string} tableName - The name of the table to fetch data from
 * @param {boolean} [fetchDeleted=false] - Whether to include soft-deleted records (is_deleted=true)
 * @param {string|null} [searchTxt=null] - Text to search across all columns (ilike %text%)
 * @param {string|string[]|null} [sortField=null] - Column(s) to sort by; can be a single string or array of strings
 * @param {number|number[]} [sortOrder=0] - Direction to sort (0=none, 1=ascending, -1=descending);
 *                                             can be a single value or array matching sortField
 * @param {number} [page=0] - Zero-based page number for pagination
 * @param {number} [pageSize=10] - Number of records per page
 * @param {boolean} [fetchAll=false] - If true, ignores pagination and returns all records
 * @param {Array<{field: string, value: any, opType?: string}>} [queryParams=[]] - Advanced filtering parameters
 * @param {boolean} [headOnly=false] - When true, only returns the count of matching records
 */
async function fetchData(
  tableName,
  fetchDeleted = false,
  searchTxt = null,
  sortField = null,
  sortOrder = 0,
  page = 0,
  pageSize = 10,
  fetchAll = false,
  queryParams = [],
  headOnly = false
) {
  

  let _data;
  let totalCount;

  try {
    const collection = await getCollection(tableName);

    let selector = {};

    // Add search text filter if provided
    if (searchTxt) {
      const schemaKeys = Object.keys(
        collection.schema.jsonSchema.properties
      ).filter((key) => !key.startsWith("_"));
      selector = {
        $or: schemaKeys.map((key) => ({
          [key]: { $regex: `.*${searchTxt}.*`, $options: "i" }
        }))
      };
    }

    // Add deleted filter if specified
    if (!fetchDeleted) {
      selector.is_deleted = { $ne: true };
    }

    // Add any additional query parameters
    if (queryParams && Array.isArray(queryParams)) {
      queryParams.forEach(({ field, value, opType }) => {
        if (value === undefined) {
          value = "";
        }

        // convert arrow notation to dot notation
        const convertedField = Helper.convertArrowToDotNotation(field);

        // Apply the appropriate comparison type
        switch (opType) {
          // Check if the field's value exactly matches the given value
          case Constants.enums.COMPARISON_TYPE.IS:
            selector[convertedField] = { $eq: value };
            break;
          // Check if the field contains the given value (array search)
          case Constants.enums.COMPARISON_TYPE.CS:
            selector[convertedField] = { $elemMatch: { $eq: value } };
            break;
          case Constants.enums.COMPARISON_TYPE.JSON_CS:
            selector[convertedField] = { $elemMatch: value };
            break;
          case Constants.enums.COMPARISON_TYPE.OR:
            if (Array.isArray(value)) {
              // Check if the field's value matches any value in the given list
              selector[convertedField] = { $in: value };
            } else {
              selector[convertedField] = { $eq: value };
            }
            break;
          // Check if the field's value is different from the given value
          case Constants.enums.COMPARISON_TYPE.NOT_EQ:
            selector[convertedField] = { $ne: value };
            break;
          // Check if the field's value is between the given range
          case Constants.enums.COMPARISON_TYPE.BETWEEN:
            if (value && value.from && value.to) {
              selector[convertedField] = { $gte: value.from, $lte: value.to };
            }
            break;
          // Check if the field's value is greater than the given value
          case Constants.enums.COMPARISON_TYPE.GT:
            selector[convertedField] = { $gt: value };
            break;
          // Check if the field's value is less than the given value
          case Constants.enums.COMPARISON_TYPE.LT:
            selector[convertedField] = { $lt: value };
            break;
          // Use exact match (eq) if no comparison type is provided
          default:
            selector[convertedField] = { $eq: value };
        }
      });
    }

    // If headOnly is true, only return the count
    if (headOnly) {
      const countQuery = collection.find({
        selector
      });
      const countDocs = await countQuery.exec();
      return countDocs.length;
    }

    // Check count cache or get count if needed
    const cacheKey = createCacheKey(
      tableName,
      fetchDeleted,
      searchTxt,
      queryParams
    );

    if (countCache.has(cacheKey)) {
      totalCount = countCache.get(cacheKey);
    } else {
      // Get count using the same query but with headOnly flag
      totalCount = await fetchData(
        tableName,
        fetchDeleted,
        searchTxt,
        null, // sortField not needed for count
        0, // sortOrder not needed for count
        0, // page not needed for count
        0, // pageSize not needed for count
        false, // fetchAll not needed for count
        queryParams,
        true // headOnly = true to get only the count
      );

      // Cache the count result
      countCache.set(cacheKey, totalCount);

      // Implement cache expiration
      setTimeout(() => {
        countCache.delete(cacheKey);
      }, 60000); // Cache expires after 1 minute
    }

    // Build the sort options and update selector for sorted fields
    const sortOptions = [];
    if (sortField && sortOrder !== 0) {
      if (Array.isArray(sortField)) {
        for (const field of sortField) {
          // Add sort field to selector to ensure it exists
          selector[field] = { $exists: true };
          sortOptions.push({
            [field]: sortOrder === 1 ? "asc" : "desc"
          });
        }
      } else {
        selector[sortField] = { $exists: true };
        sortOptions.push({
          [sortField]: sortOrder === 1 ? "asc" : "desc"
        });
      }
    }

    // Add remote_updated_at to selector and sort options
    selector.remote_updated_at = { $exists: true };
    sortOptions.push({ remote_updated_at: "desc" });
    // Build the query for data
    let query = collection.find({
      selector,
      sort: sortOptions,
      ...(!fetchAll && {
        skip: page * pageSize,
        limit: pageSize
      })
    });
    const forms = await query.exec();
    _data = forms.map((form) => form.toJSON());
  } catch (error) {
    exception.catchException(error);
    return {
      error: `An unexpected error occurred : ${error}`
    };
  }

  return {
    data: _data,
    totalItems: totalCount,
    error: null
  };
}

/**
 * Retrieves a single record from the specified RxDB collection by its ID.
 *
 * @param {string} tableName - The name of the collection to fetch data from
 * @param {string} id - The ID of the record to retrieve
 *
 * @returns {Promise<Object>} Returns an object with the following properties:
 *   - error: Error message if any, or null if successful
 *   - data: The retrieved record, or null if not found or error occurred
 *
 * @example
 * const result = await getData('users', '123');
 */
async function getData(tableName, id) {
  let _data;
  try {
    const collection = await getCollection(tableName);
    const form = await collection
      .findOne({
        selector: {
          id
        }
      })
      .exec();
    // console.log("PULLED RXDB DATA --> :", tableName, id, form);
    _data = form ? form.toJSON() : null;
  } catch (error) {
    // If collection doesn't exist, return null data instead of throwing error
    if (error.message && error.message.includes("not found")) {
      console.warn(`Collection ${tableName} not found, returning null data`);
      return {
        data: null,
        error: null
      };
    }
    exception.catchException(error);
    return {
      error: error || "An unexpected error occurred",
      data: null
    };
  }

  return {
    data: _data,
    error: null
  };
}

/**
 * Fetches the next sequence value for a given sequence name.
 *
 * @param {string} sequenceName - The name of the sequence to get the next value for
 * @returns {Promise<Object>} Returns an object with the following properties:
 *   - data: The next sequence value
 *   - error: Error message if any, or null if successful
 */
async function getNextSequenceValue(sequenceName) {
  // Implementation will be added
  // Consider how we will do this in RxDB offline, maybe some sort of queue for docs that need sequence numbers that will be processed when we come back online?
}

/**
 * Fetches distinct values for specified columns in an RxDB collection.
 * Performs the operation in memory by collecting unique values from all documents.
 *
 * @param {string} tableName - The name of the collection to query
 * @param {string|string[]} columnNames - Single column name or array of column names to get distinct values for
 *
 * @returns {Promise<Object>} Returns an object with the following properties:
 *   - data: Array of objects, each containing distinct values for a requested column
 *   - error: Error message if any, or null if successful
 *
 * @example
 * // Get distinct values for a single column
 * const result = await getDistinctColumnValues('users', 'status');
 *
 * @example
 * // Get distinct values for multiple columns
 * const result = await getDistinctColumnValues('users', ['status', 'role']);
 */
async function getDistinctColumnValues(tableName, columnNames) {
  console.warn(
    `------------- USING RXDB TO GET DISTINCT COLUMN VALUES : ${tableName} -------------`
  );
  try {
    const columns = Array.isArray(columnNames) ? columnNames : [columnNames];

    if (columns.length === 0) {
      throw new Error("columnNames must be a non-empty string or array");
    }

    const collection = await getCollection(tableName);

    // Get all documents from the collection
    const docs = await collection.find().exec();
    // Create a Set for each column to store unique values
    const distinctValues = {};
    columns.forEach((column) => {
      distinctValues[column] = new Set();
    });

    // Iterate through documents and collect unique values
    docs.forEach((doc) => {
      const jsonDoc = doc.toJSON();
      columns.forEach((column) => {
        const value = Helper.extractFromJsonPath(jsonDoc, column);
        if (Helper.isValueExist(value) && value !== "-") {
          distinctValues[column].add(value);
        }
      });
    });
    // Convert Sets to arrays and create the result object
    const result = [];
    columns.forEach((column) => {
      const values = Array.from(distinctValues[column]);
      values.forEach((value) => {
        // extract the column name from the json_path
        const columnName = column ? column.split("->>").pop() : column;
        const obj = {};
        obj[columnName] = value;
        result.push(obj);
      });
    });

    return {
      data: result,
      error: null
    };
  } catch (error) {
    exception.catchException(error);
    return {
      data: null,
      error: error.message || "An unexpected error occurred"
    };
  }
}

/**
 * Deletes records from the specified RxDB collection based on query parameters.
 * Supports various comparison types for flexible filtering conditions.
 *
 * @param {string} tableName - The name of the collection to delete data from
 * @param {Array<{field: string, value: any, opType?: string}>} queryParams - Array of filter parameters:
 *   - field: The column name to filter on
 *   - value: The value to filter by
 *   - opType: Optional comparison type from Constants.enums.COMPARISON_TYPE (IS, CS, OR, NOT_EQ, BETWEEN, GT, LT, or default=EQ)
 *
 * @returns {Promise<Object>} Returns an object with the following properties:
 *   - data: The deleted records
 *   - error: Error message if any, or null if successful
 *
 * @example
 * // Delete records where status is 'completed'
 * const result = await deleteData('tasks', [
 *   { field: 'status', value: 'completed' }
 * ]);
 *
 * @example
 * // Delete records with multiple conditions
 * const result = await deleteData('orders', [
 *   { field: 'status', value: 'cancelled', opType: 'IS' },
 *   { field: 'amount', value: { from: 100, to: 500 }, opType: 'BETWEEN' }
 * ]);
 */
async function deleteData(tableName, queryParams) {
  console.warn(
    `------------- USING RXDB TO DELETE DATA : ${tableName} -------------`
  );
  try {
    const collection = await getCollection(tableName);
    let selector = {};

    // Add any additional query parameters
    if (queryParams && Array.isArray(queryParams)) {
      queryParams.forEach(({ field, value, opType }) => {
        // convert arrow notation to dot notation
        const convertedField = Helper.convertArrowToDotNotation(field);

        if (!Helper.isValueExist(value)) {
          return;
        }
        // Apply the appropriate comparison type
        switch (opType) {
          // Check if the field's value exactly matches the given value
          case Constants.enums.COMPARISON_TYPE.IS:
            selector[convertedField] = { $eq: value };
            break;
          // Check if the field contains the given value (array search)
          case Constants.enums.COMPARISON_TYPE.CS:
            selector[convertedField] = { $elemMatch: { $eq: value } };
            break;
          case Constants.enums.COMPARISON_TYPE.JSON_CS:
            selector[convertedField] = { $elemMatch: value };
            break;
          case Constants.enums.COMPARISON_TYPE.OR:
            if (Array.isArray(value)) {
              selector[convertedField] = { $in: value };
            } else {
              selector[convertedField] = { $eq: value };
            }
            break;
          // Check if the field's value is different from the given value
          case Constants.enums.COMPARISON_TYPE.NOT_EQ:
            selector[convertedField] = { $ne: value };
            break;
          // Check if the field's value is between the given range
          case Constants.enums.COMPARISON_TYPE.BETWEEN:
            if (value && value.from && value.to) {
              selector[convertedField] = { $gte: value.from, $lte: value.to };
            }
            break;
          // Check if the field's value is greater than the given value
          case Constants.enums.COMPARISON_TYPE.GT:
            selector[convertedField] = { $gt: value };
            break;
          // Check if the field's value is less than the given value
          case Constants.enums.COMPARISON_TYPE.LT:
            selector[convertedField] = { $lt: value };
            break;
          // Use exact match (eq) if no comparison type is provided
          default:
            selector[convertedField] = { $eq: value };
        }
      });
    }

    // Find and remove all matching documents
    const docs = await collection
      .find({
        selector
      })
      .exec();

    // Remove each document
    const removedDocs = await Promise.all(docs.map((doc) => doc.remove()));

    return {
      data: removedDocs.map((doc) => doc.toJSON()),
      error: null
    };
  } catch (error) {
    exception.catchException(error);
    return {
      data: null,
      error: error.message || "An unexpected error occurred"
    };
  }
}

export default {
  insertData,
  fetchData,
  updateData,
  getData,
  getNextSequenceValue,
  getDistinctColumnValues,
  deleteData,
  subscribeToDocument
};
